import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { openFile, byteSize, Translate, getSortState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './blog.reducer';

export const Blog = () => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(location, ITEMS_PER_PAGE, 'id'), location.search)
  );

  const blogList = useAppSelector(state => state.blog.entities);
  const loading = useAppSelector(state => state.blog.loading);
  const totalItems = useAppSelector(state => state.blog.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (location.search !== endURL) {
      navigate(`${location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [location.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="blog-heading" data-cy="BlogHeading">
        <Translate contentKey="fitengineerBackendV2App.blog.home.title">Blogs</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="fitengineerBackendV2App.blog.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/blog/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="fitengineerBackendV2App.blog.home.createLabel">Create new Blog</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {blogList && blogList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="fitengineerBackendV2App.blog.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('blogTitle')}>
                  <Translate contentKey="fitengineerBackendV2App.blog.blogTitle">Blog Title</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('blogTitle')} />
                </th>
                <th className="hand" onClick={sort('blogContent')}>
                  <Translate contentKey="fitengineerBackendV2App.blog.blogContent">Blog Content</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('blogContent')} />
                </th>
                <th className="hand" onClick={sort('image1')}>
                  <Translate contentKey="fitengineerBackendV2App.blog.image1">Image 1</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('image1')} />
                </th>
                <th className="hand" onClick={sort('image2')}>
                  <Translate contentKey="fitengineerBackendV2App.blog.image2">Image 2</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('image2')} />
                </th>
                <th className="hand" onClick={sort('image3')}>
                  <Translate contentKey="fitengineerBackendV2App.blog.image3">Image 3</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('image3')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {blogList.map((blog, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/blog/${blog.id}`} color="link" size="sm">
                      {blog.id}
                    </Button>
                  </td>
                  <td>{blog.blogTitle}</td>
                  <td>{blog.blogContent}</td>
                  <td>
                    {blog.image1 ? (
                      <div>
                        {blog.image1ContentType ? (
                          <a onClick={openFile(blog.image1ContentType, blog.image1)}>
                            <img src={`data:${blog.image1ContentType};base64,${blog.image1}`} style={{ maxHeight: '30px' }} />
                            &nbsp;
                          </a>
                        ) : null}
                        <span>
                          {blog.image1ContentType}, {byteSize(blog.image1)}
                        </span>
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {blog.image2 ? (
                      <div>
                        {blog.image2ContentType ? (
                          <a onClick={openFile(blog.image2ContentType, blog.image2)}>
                            <img src={`data:${blog.image2ContentType};base64,${blog.image2}`} style={{ maxHeight: '30px' }} />
                            &nbsp;
                          </a>
                        ) : null}
                        <span>
                          {blog.image2ContentType}, {byteSize(blog.image2)}
                        </span>
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {blog.image3 ? (
                      <div>
                        {blog.image3ContentType ? (
                          <a onClick={openFile(blog.image3ContentType, blog.image3)}>
                            <img src={`data:${blog.image3ContentType};base64,${blog.image3}`} style={{ maxHeight: '30px' }} />
                            &nbsp;
                          </a>
                        ) : null}
                        <span>
                          {blog.image3ContentType}, {byteSize(blog.image3)}
                        </span>
                      </div>
                    ) : null}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/blog/${blog.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/blog/${blog.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/blog/${blog.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="fitengineerBackendV2App.blog.home.notFound">No Blogs found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={blogList && blogList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Blog;
